import { create } from 'zustand';
import api from '@/utils/api';
import {
  ApiKey,
  ApiKeyAuditEvent,
  CreateApiKeyInput,
  CreatedApiKey,
  UpdateApiKeyInput,
} from '@/lib/types';

interface ApiKeysStore {
  apiKeys: ApiKey[];
  loading: boolean;
  error: string | null;
  loadApiKeys: (workspaceId: string | number) => Promise<void>;
  createApiKey: (workspaceId: string | number, input: CreateApiKeyInput) => Promise<CreatedApiKey>;
  updateApiKey: (
    workspaceId: string | number,
    id: number,
    input: UpdateApiKeyInput,
  ) => Promise<void>;
  rotateApiKey: (workspaceId: string | number, id: number) => Promise<CreatedApiKey>;
  revokeApiKey: (workspaceId: string | number, id: number) => Promise<void>;
  loadAuditEvents: (workspaceId: string | number, id: number) => Promise<ApiKeyAuditEvent[]>;
}

const useApiKeysStore = create<ApiKeysStore>()((set, get) => ({
  apiKeys: [],
  loading: false,
  error: null,
  loadApiKeys: async (workspaceId) => {
    try {
      set({ loading: true, error: null });
      const response = await api.get(`/workspace/${workspaceId}/api-keys`);
      set({ apiKeys: (response.data.resource as ApiKey[]) ?? [], loading: false });
    } catch (e: any) {
      set({
        error: e?.response?.data?.error || 'Failed to load API keys',
        loading: false,
      });
    }
  },
  createApiKey: async (workspaceId, input) => {
    const response = await api.post(`/workspace/${workspaceId}/api-keys`, {
      name: input.name,
      scopes: input.scopes,
      expires_at: input.expires_at,
    });
    const created = response.data.resource as CreatedApiKey;
    set({ apiKeys: [created.api_key, ...get().apiKeys] });
    return created;
  },
  updateApiKey: async (workspaceId, id, input) => {
    await api.patch(`/workspace/${workspaceId}/api-keys/${id}`, input);
    // Refresh the row from the server so we pick up the canonical name/scopes.
    await get().loadApiKeys(workspaceId);
  },
  rotateApiKey: async (workspaceId, id) => {
    const response = await api.post(`/workspace/${workspaceId}/api-keys/${id}/rotate`);
    const created = response.data.resource as CreatedApiKey;
    // The old row was revoked server-side; reload to reflect both.
    await get().loadApiKeys(workspaceId);
    return created;
  },
  revokeApiKey: async (workspaceId, id) => {
    await api.delete(`/workspace/${workspaceId}/api-keys/${id}`);
    const now = new Date().toISOString();
    set({
      apiKeys: get().apiKeys.map((k) => (k.id === id ? { ...k, revoked_at: now } : k)),
    });
  },
  loadAuditEvents: async (workspaceId, id) => {
    const response = await api.get(`/workspace/${workspaceId}/api-keys/${id}/audit`);
    return (response.data.resource as ApiKeyAuditEvent[]) ?? [];
  },
}));

export default useApiKeysStore;
